import { Button } from '@/components/ui/button';
import { Heart } from 'lucide-react';
import React, { useMemo, useEffect } from 'react';
import { BlurImage } from '@/components/shared/BlurImage';
import { useBlurPlaceholder } from '@/hooks/use-blur-placeholder';
import { convertUrlToWebP } from '@/lib/image-conversion';
import { useImagePreload } from '@/components/context/ImagePreloadContext';
import { isMobileDevice } from '@/lib/lcp-optimization';

interface DestinationImageProps {
  imageUrl: string | null;
  name: string;
  isFollowed: boolean;
  hasBeach?: boolean;
  onToggleFollow: (e: React.MouseEvent) => void;
  isLcp?: boolean;
}

// Get beach and city images with proper sizing based on device
const getOptimizedImageUrls = (baseUrls: string[], isMobile: boolean) => {
  if (isMobile) {
    return baseUrls.map(url => 
      `${url}?auto=format&fit=crop&w=480&h=360&q=70`
    );
  } else {
    return baseUrls.map(url => 
      `${url}?auto=format&fit=crop&w=600&h=450&q=80`
    );
  }
};

// Beach images to cycle through randomly for beach destinations
const getBeachImages = () => {
  const baseUrls = [
    'https://images.unsplash.com/photo-1500375592092-40eb2168fd21',
    'https://images.unsplash.com/photo-1482938289607-e9573fc25ebb',
    'https://images.unsplash.com/photo-1433086966358-54859d0ed716'
  ];
  
  return getOptimizedImageUrls(baseUrls, isMobileDevice());
};

// Other fallback images for non-beach destinations
const getCityImages = () => {
  const baseUrls = [
    'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df',
    'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b',
    'https://images.unsplash.com/photo-1444723121867-7a241cacace9'
  ];
  
  return getOptimizedImageUrls(baseUrls, isMobileDevice());
};

export const DestinationImage = ({ 
  imageUrl, 
  name, 
  isFollowed, 
  hasBeach = false,
  onToggleFollow,
  isLcp = false
}: DestinationImageProps) => {
  const { preloadLcpImage } = useImagePreload();
  const isMobile = isMobileDevice();
  
  // Get beach and city images arrays - memoized to avoid recalculation
  const BEACH_IMAGES = useMemo(() => getBeachImages(), []);
  const CITY_IMAGES = useMemo(() => getCityImages(), []);
  
  // Get a deterministic but random-looking image based on the destination name
  const getDefaultImage = useMemo(() => {
    // Generate a hash from the name to consistently get the same image for each destination
    const charSum = name.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
    
    if (hasBeach) {
      // For beach destinations without images
      const index = charSum % BEACH_IMAGES.length;
      return convertUrlToWebP(BEACH_IMAGES[index]);
    } else {
      // For other destinations without images
      const index = charSum % CITY_IMAGES.length;
      return convertUrlToWebP(CITY_IMAGES[index]);
    }
  }, [name, hasBeach, BEACH_IMAGES, CITY_IMAGES]);

  // Determine which image to display, converting to WebP when possible
  const displayImage = !imageUrl ? getDefaultImage : convertUrlToWebP(imageUrl);
  
  // Debug logging
  useEffect(() => {
    console.log('DestinationImage:', { name, imageUrl, displayImage, hasBeach });
  }, [name, imageUrl, displayImage, hasBeach]);
  
  // Generate blur placeholder for the image
  const { blurDataURL } = useBlurPlaceholder(displayImage);

  // Preload the LCP image if needed
  useEffect(() => {
    if (isLcp && displayImage) {
      preloadLcpImage(displayImage);
    }
  }, [displayImage, isLcp, preloadLcpImage]);
  
  return (
    <div className="relative w-full h-full">
      <BlurImage
        src={displayImage}
        alt={name}
        className="absolute inset-0 w-full h-full object-cover"
        placeholder="/placeholder.svg"
        priority={isLcp}
        isLcp={isLcp}
        blurDataURL={blurDataURL}
      />
      
      <Button
        variant="ghost"
        size="icon"
        className={`absolute top-2 right-2 z-10 ${isFollowed ? 'text-primary' : ''}`}
        onClick={onToggleFollow}
      >
        <Heart className="w-5 h-5" fill={isFollowed ? 'currentColor' : 'none'} />
      </Button>
    </div>
  );
};
